import { Engine } from '@syqel/ota-client'
import { DetailGovernor, NO_DETAIL, SURFACES, sdkCalibrator, type Calibration, type DetailControl, type Surface } from '../renderPolicy'
import { calibrationOf, priorDetailOf } from '../worldCost'

/**
 * THE DETAIL GOVERNOR, one PER WORLD (renderPolicy.ts — detail is the only lever; resolution is native). What a
 * world costs differs ~10× across the library, so each world gets its own setting: calibrated once per device
 * and remembered (worldCost.ts), resumed when the world returns.
 */
export interface WorldGovernor {
  /** one frame's real interval; `settled` = the governed world was alone on screen for it; `clean` = no world was
   *  built, compiled or torn down across it (LifecycleLedger) */
  tick: (realDt: number, settled: boolean, clean?: boolean) => void
  /** frames per second over the last half second (any frame — for the perf probe) */
  readonly fps: number
  /** the detail in force */
  readonly detail: number
  /** the governed world's setting on this device, once known (worldCost.ts remembers it) */
  readonly result: Calibration | null
  /** a world settled on screen takes the governor */
  settle: (worldId: string) => void
}

/** `detail` — the stage's live detail control: the caller's VisualHost (engineApi 1.20). */
export function createWorldGovernor(surface: Surface, detail: DetailControl = NO_DETAIL): WorldGovernor {
  const policy = SURFACES[surface]
  const calibrate = sdkCalibrator(Engine.DetailCalibrator) // the SDK's DetailCalibrator, under this policy's window
  let govWorld = ''
  let gov = new DetailGovernor(policy, calibrate, detail)
  let frames = 0
  let acc = 0
  let fps = 0
  return {
    tick: (realDt, settled, clean = true) => {
      if (realDt > 0) { frames += 1; acc += realDt; if (acc >= 0.5) { fps = frames / acc; frames = 0; acc = 0 } }
      gov.tick(realDt, settled, clean)
    },
    get fps() { return fps },
    get detail() { return gov.current },
    get result() { return gov.result },
    settle: (worldId) => {
      if (worldId === govWorld) return
      govWorld = worldId
      gov = new DetailGovernor(policy, calibrate, detail, calibrationOf(worldId), priorDetailOf(worldId))
    }
  }
}
